import { useEffect, useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import Pagination from "../../components/Pagination";
import GeofenceMap from "../../components/GeofenceMap";
import { useApi } from "../../hooks/useApi";
import {
  listGeofences,
  createGeofence,
  updateGeofence,
  deleteGeofence,
} from "../../api/geofenceService";
import { listEmployees } from "../../api/employeeService";
import { paginatedRows, paginatedMeta } from "../../utils/pagination";
import { serialNumber } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";

export default function GeofencesPage() {
  const [drawer, setDrawer] = useState(null); // null | { mode:'create' } | { mode:'edit', geofence }
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const geofences = useApi(() => listGeofences({ page, per_page: perPage }), [page, perPage]);
  const employees = useApi(() => listEmployees({ per_page: 100, status: "active" }));

  const rows = paginatedRows(geofences.data);
  const meta = paginatedMeta(geofences.data);
  const limits = geofences.data?.limits || { used: 0, max: 0 };
  const empRows = paginatedRows(employees.data);
  const atCap = limits.max === 0 || limits.used >= limits.max;

  const onDelete = async (g) => {
    if (!window.confirm(`Delete "${g.name}"? Employees assigned to it will be able to sign in anywhere again.`)) return;
    try {
      await deleteGeofence(g.id);
      showToast("success", "Geofence deleted");
      geofences.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Geofences</h1>
          <div className="page-sub">
            {limits.used} / {limits.max} geofences · max is your number of employees
          </div>
        </div>
        <div className="page-actions">
          <button
            className="btn btn-primary"
            onClick={() => setDrawer({ mode: "create" })}
            disabled={atCap}
            title={atCap ? "You've reached the geofence limit (one per employee)." : undefined}
          >
            <Icon name="plus" size={14} /> New geofence
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body no-pad">
          {geofences.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={4} /></div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon="map-pin"
              title="No geofences yet"
              hint={limits.max === 0 ? "Add an employee first — the fence limit tracks your team size." : "Draw an area on the map and assign employees to it."}
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th style={{ width: 50 }} className="t-num">#</th>
                  <th>Name</th>
                  <th style={{ textAlign: "center" }}>Points</th>
                  <th>Assigned employees</th>
                  <th>Status</th>
                  <th style={{ width: 90 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((g, i) => (
                  <tr key={g.id}>
                    <td className="t-num t-muted">{serialNumber(meta, i)}</td>
                    <td>
                      <div className="row gap-sm">
                        <span className="sb-icon" style={{ color: "var(--primary-600, #4f46e5)" }}>
                          <Icon name="map-pin" size={15} />
                        </span>
                        <span className="t-primary">{g.name}</span>
                      </div>
                    </td>
                    <td style={{ textAlign: "center" }} className="t-muted">{g.points?.length ?? 0}</td>
                    <td>
                      {g.employees?.length ? (
                        <div className="row gap-sm" style={{ flexWrap: "wrap" }}>
                          {g.employees.map((e) => (
                            <span key={e.id} className="badge badge-neutral" title={e.employee_id}>{e.name}</span>
                          ))}
                        </div>
                      ) : (
                        <span className="t-muted text-xs">Unassigned</span>
                      )}
                    </td>
                    <td>
                      {g.is_active
                        ? <span className="badge badge-success">Active</span>
                        : <span className="badge badge-neutral">Off</span>}
                    </td>
                    <td>
                      <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
                        <button className="icon-btn" title="Edit" onClick={() => setDrawer({ mode: "edit", geofence: g })}>
                          <Icon name="edit" size={13} />
                        </button>
                        <button className="icon-btn" title="Delete" onClick={() => onDelete(g)} style={{ color: "var(--danger)" }}>
                          <Icon name="trash" size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pagination
            meta={meta}
            page={page}
            onPageChange={setPage}
            perPage={perPage}
            onPerPageChange={setPerPage}
            disabled={geofences.loading}
          />
        </div>
      </div>

      <GeofenceDrawer
        open={!!drawer}
        mode={drawer?.mode}
        geofence={drawer?.geofence}
        employees={empRows}
        onClose={() => setDrawer(null)}
        onSaved={() => {
          setDrawer(null);
          geofences.reload();
        }}
      />
    </>
  );
}

function GeofenceDrawer({ open, mode, geofence, employees, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [points, setPoints] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && geofence) {
      setName(geofence.name || "");
      setIsActive(geofence.is_active !== false);
      setPoints(Array.isArray(geofence.points) ? geofence.points : []);
      setAssigned((geofence.employees || []).map((e) => e.id));
    } else {
      setName("");
      setIsActive(true);
      setPoints([]);
      setAssigned([]);
    }
    setErrors({});
  }, [open, mode, geofence]);

  const toggleEmployee = (id) =>
    setAssigned((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));

  const submit = async () => {
    const nextErrors = {};
    if (!name.trim()) nextErrors.name = "Give this area a name.";
    if (!Array.isArray(points) || points.length < 3) nextErrors.points = "Draw an area with at least 3 points on the map.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        points,
        is_active: isActive,
        employee_ids: assigned,
      };
      if (mode === "edit") {
        await updateGeofence(geofence.id, payload);
        showToast("success", "Geofence updated");
      } else {
        await createGeofence(payload);
        showToast("success", "Geofence created");
      }
      onSaved?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      wide
      title={mode === "edit" ? `Edit ${geofence?.name || "geofence"}` : "New geofence"}
      subtitle="Draw the allowed area and assign employees. Assigned employees can only sign in from inside it."
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Create"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Name</label>
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Head Office" />
          {errors.name && <div className="field-error">{Array.isArray(errors.name) ? errors.name[0] : errors.name}</div>}
        </div>

        <div className="field">
          <label className="field-label">Area</label>
          <GeofenceMap value={points} onChange={setPoints} height={340} />
          {errors.points && <div className="field-error">{Array.isArray(errors.points) ? errors.points[0] : errors.points}</div>}
          {points.length >= 3 && !errors.points && (
            <div className="field-hint">{points.length} points captured.</div>
          )}
        </div>

        <div className="row gap-sm">
          <input id="geo-active" type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          <label htmlFor="geo-active" style={{ flex: 1, cursor: "pointer", marginBottom: 0 }}>
            <div className="t-primary text-sm fw-500">Active</div>
            <div className="t-muted text-xs" style={{ marginTop: 2 }}>
              When off, this fence is ignored and its employees can sign in anywhere.
            </div>
          </label>
        </div>

        <div className="field">
          <label className="field-label">Assign employees</label>
          <div className="field-hint" style={{ marginBottom: 6 }}>
            Only the employees you tick here are restricted to this area.
          </div>
          <div className="col gap-sm">
            {employees.length === 0 ? (
              <div className="t-muted text-xs">No active employees to assign.</div>
            ) : (
              employees.map((e) => {
                const id = `geo-emp-${e.id}`;
                return (
                  <label key={e.id} htmlFor={id} className="row gap-sm" style={{ cursor: "pointer", alignItems: "flex-start" }}>
                    <input id={id} type="checkbox" checked={assigned.includes(e.id)} onChange={() => toggleEmployee(e.id)} style={{ marginTop: 3 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="t-primary text-sm fw-500">{e.name}</div>
                      <div className="t-muted text-xs" style={{ marginTop: 2 }}>
                        <span className="t-mono">{e.employee_id}</span>{e.department?.name ? ` · ${e.department.name}` : ""}
                      </div>
                    </div>
                  </label>
                );
              })
            )}
          </div>
        </div>
      </div>
    </Drawer>
  );
}
